import { FC, useMemo, useState } from 'react'
import { Input } from 'reactstrap'
import { LabeledCheckboxInput } from 'web/components/generic/LabeledCheckbox'
import { WithTooltip } from 'web/components/generic/WithTooltip'
import { GenerateNoteOptions } from 'web/data/setup-note-exporter'
import { useToggle } from 'web/hooks/useToggle'
import { joinLines } from 'web/utils/utils'

type ExportNoteTabProps = {
    generateNoteString: (params: GenerateNoteOptions) => string
}

export const ExportNoteTab: FC<ExportNoteTabProps> = ({ generateNoteString }) => {

    const [renderModeRadio, setRenderModeRadio] = useState<"names" | "icons_names" | "icons">('icons')
    const [isToggledUnassigned, toggleUnassigned] = useToggle(false)
    const [isDynamicTimestamp, toggleDynamicTimestamp] = useToggle(true)
    const [addStaticTimers, toggleAddStaticTimers] = useToggle(true)
    const [useConditionalTimers, toggleUseConditionalTimers] = useToggle(true)
    const [isBreakOnSegment, toggleBreakOnSegment] = useToggle(true)

    const noteString = useMemo(() => {
        return generateNoteString({
            breakOnSegment: isBreakOnSegment,
            ertRenderMode: renderModeRadio,
            ertTimestamps: isDynamicTimestamp,
            omitUnassigned: !isToggledUnassigned,
            addStaticTimers,
            useConditionalTimers,
        })
    }, [generateNoteString, renderModeRadio, isToggledUnassigned, isDynamicTimestamp, isBreakOnSegment, addStaticTimers, useConditionalTimers])

    return (
        <>
            <div className="mb-3 d-flex align-items-start space-mx-3">

                <div className="mr-3 d-flex flex-column flex-grow-1">
                    <label className="small">{"General"}</label>
                    <div className="d-flex flex-column justify-content-start space-my-2 p-3 border bg-light rounded">
                        <LabeledCheckboxInput
                            small
                            label={"Include unassigned events"}
                            checked={isToggledUnassigned}
                            onChange={toggleUnassigned}
                        />
                        <LabeledCheckboxInput
                            small
                            label={"Empty line between segments"}
                            checked={isBreakOnSegment}
                            onChange={toggleBreakOnSegment}
                        />
                    </div>
                </div>

                <div className="d-flex flex-column flex-grow-1">
                    <label className="small">{"Exorsus Raid Tools"}</label>
                    <div className="d-flex flex-column justify-content-start space-my-2 p-3 bg-light border rounded">
                        <LabeledCheckboxInput
                            small
                            label={"Dynamic timestamps"}
                            checked={isDynamicTimestamp}
                            onChange={toggleDynamicTimestamp}
                        />

                        <WithTooltip place="left">
                            {ttId => (
                                <div
                                    data-for={ttId}
                                    data-tip={joinLines([
                                        `If you've added conditions to your segments, checking this`,
                                        `option will generate timers relative to those conditions.`
                                    ])}
                                >
                                    <LabeledCheckboxInput
                                        disabled={!isDynamicTimestamp}
                                        small
                                        label={"Create conditional timers"}
                                        checked={useConditionalTimers}
                                        onChange={toggleUseConditionalTimers}
                                    />
                                </div>
                            )}
                        </WithTooltip>

                        <WithTooltip place="left">
                            {ttId => (
                                <div
                                    data-for={ttId}
                                    data-tip={joinLines([
                                        `When using conditions, the timestamps in the note`,
                                        `will be relative to each condition and not to the start`,
                                        `of the fight. Check this to add additional timestamps`,
                                        `that are relative to the start of the fight.`
                                    ])}
                                >
                                    <LabeledCheckboxInput
                                        disabled={!isDynamicTimestamp || !useConditionalTimers}
                                        small
                                        label={"Include static timers for conditions"}
                                        checked={addStaticTimers}
                                        onChange={toggleAddStaticTimers}
                                    />
                                </div>
                            )}
                        </WithTooltip>

                        <div className="d-flex flex-column small">
                            <div className="d-flex align-items-center">
                                <input
                                    checked={renderModeRadio === "names"}
                                    name="rb_grp_render_mode"
                                    id="rb_names"
                                    type="radio"
                                    className="mr-2"
                                    onChange={() => {
                                        setRenderModeRadio('names')
                                    }}
                                />
                                <label className="mb-0" htmlFor="rb_names">
                                    {"Names"}
                                </label>
                            </div>
                            <div className="d-flex align-items-center">
                                <input
                                    checked={renderModeRadio === "icons_names"}
                                    name="rb_grp_render_mode"
                                    id="rb_icons_names"
                                    type="radio"
                                    className="mr-2"
                                    onChange={() => {
                                        setRenderModeRadio('icons_names')
                                    }}
                                />
                                <label className="mb-0" htmlFor="rb_icons_names">
                                    {"Icons + Names"}
                                </label>
                            </div>
                            <div className="d-flex align-items-center">
                                <input
                                    checked={renderModeRadio === "icons"}
                                    name="rb_grp_render_mode"
                                    id="rb_icons"
                                    type="radio"
                                    className="mr-2"
                                    onChange={() => {
                                        setRenderModeRadio('icons')
                                    }}
                                />
                                <label className="mb-0" htmlFor="rb_icons">
                                    {"Icons"}
                                </label>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Input
                type="textarea"
                readOnly={true}
                value={noteString}
                style={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    minHeight: "500px"
                }}
            />
        </>
    )
}
